import { useEffect } from 'react'
import { captureYandexClickId, ensureYandexDataLayer, resolveCounterId } from '../analytics/yandexMetrika'

declare global {
  interface Window {
    ym?: (...args: any[]) => void
    __fravaiYmScriptLoaded?: boolean
    __fravaiYmInitialized?: boolean
  }
}

function ensureYmStub() {
  if (typeof window.ym === 'function') return
  const stub: any = (...args: any[]) => {
    ;(stub.a = stub.a || []).push(args)
  }
  stub.l = Date.now()
  window.ym = stub
}

function loadYandexScript(counterId: number) {
  if (window.__fravaiYmScriptLoaded) return

  const existing = document.querySelector<HTMLScriptElement>(`script[src*="mc.yandex.ru/metrika/tag.js?id=${counterId}"]`)
  if (existing) {
    window.__fravaiYmScriptLoaded = true
    return
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`
  script.dataset.fravaiYm = '1'
  script.onload = () => {
    window.__fravaiYmScriptLoaded = true
  }
  document.head.appendChild(script)
}

function initYandexMetrika() {
  if (typeof window === 'undefined' || window.__fravaiYmInitialized) return
  const counterId = resolveCounterId()
  ensureYandexDataLayer()
  ensureYmStub()
  loadYandexScript(counterId)
  window.ym?.(counterId, 'init', {
    ssr: true,
    webvisor: false,
    trackHash: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    accurateTrackBounce: true,
    trackLinks: true,
  })
  window.__fravaiYmInitialized = true
}

export default function YandexMetrikaBootstrap() {
  useEffect(() => {
    if (import.meta.env.VITE_YM_ENABLED === 'false') return
    if (typeof window === 'undefined') return

    captureYandexClickId()
    let timeoutId: number | undefined
    let idleId: number | undefined

    const run = () => initYandexMetrika()
    const onFirstInteraction = () => run()

    window.addEventListener('pointerdown', onFirstInteraction, { once: true, passive: true })
    window.addEventListener('keydown', onFirstInteraction, { once: true, passive: true })

    if ('requestIdleCallback' in window) {
      idleId = (window as any).requestIdleCallback(run, { timeout: 3000 })
    } else {
      timeoutId = window.setTimeout(run, 2500)
    }

    return () => {
      window.removeEventListener('pointerdown', onFirstInteraction)
      window.removeEventListener('keydown', onFirstInteraction)
      if (typeof idleId === 'number' && 'cancelIdleCallback' in window) {
        ;(window as any).cancelIdleCallback(idleId)
      }
      if (typeof timeoutId === 'number') {
        window.clearTimeout(timeoutId)
      }
    }
  }, [])

  return null
}
